// Runner de sincronización (F4 #52). Vacía la outbox (outbox.ts) FIFO y secuencial contra el
// PUSH del backend (POST /api/v1/sync/mutations), aplica el veredicto de cada mutación y, al
// terminar, reconcilia las lecturas afectadas. El servidor es la única autoridad: revalida y
// audita cada mutación; el runner solo transporta, ordena y reescribe referencias locales.
//
// Diseño (PLAN.md §Sincronización):
//   - FIFO secuencial (una mutación por petición): respeta las dependencias causales (crear un
//     LOTO antes del bloqueo que cuelga de él) sin coordinación extra; el id de servidor del
//     alta ya está en el idMap cuando se procesa la mutación dependiente.
//   - Single-flight: un único flush a la vez (reconexión + heartbeat + montaje pueden dispararlo
//     a la par). Reintento idempotente por client_mutation_id, así que reenviar es seguro.
//   - Fail-safe ante red: si una petición falla (red caída, 401), se corta el flush dejando las
//     mutaciones restantes pendientes para el próximo reconnect; nunca se descarta trabajo.

import type { QueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { dashboardQueryKey } from "@/lib/dashboard";
import { lotosQueryKey } from "@/lib/lotos";
import { getOnline } from "@/lib/online-status";
import {
  getIdMapping,
  getTempIdByClientUuid,
  listAttachments,
  nextPending,
  putIdMapping,
  removeAttachment,
  removeMutation,
  updateMutation,
} from "@/lib/outbox";

// Espejo del contrato del PUSH (syncapi/push_dto.go). El cuerpo es un lote (aquí de una sola
// mutación); la respuesta trae un resultado por mutación en el mismo orden.
type PushMutation = {
  client_mutation_id: string;
  entity: string;
  op: string;
  client_uuid: string;
  base_version: number | null;
  payload: Record<string, unknown>;
};

type IDMapping = { entity: string; client_uuid: string; server_id: number };

type MutationResult = {
  client_mutation_id: string;
  status: "applied" | "conflict" | "rejected";
  server_version?: number;
  error_code?: string;
  id_mapping?: IDMapping;
};

type PushResponse = { results: MutationResult[] };

/**
 * FlushSummary resume lo que hizo un flush: cuántas mutaciones se aplicaron, chocaron o se
 * rechazaron, cuántas quedaron bloqueadas por una dependencia sin resolver, y si el flush se
 * cortó por un fallo de red (quedando pendientes para el próximo intento).
 */
export type FlushSummary = {
  applied: number;
  conflict: number;
  rejected: number;
  blocked: number;
  attachments: number;
  stopped: boolean;
};

// Guard single-flight a nivel de módulo: evita flushes solapados dentro de la misma pestaña.
let flushing = false;

/**
 * flushOutbox procesa la outbox FIFO contra el PUSH y reconcilia las lecturas al terminar.
 * Es idempotente y seguro de reintentar: las mutaciones ya aplicadas se resuelven por el libro
 * de idempotencia del servidor (client_mutation_id) y por la unicidad de client_uuid. Devuelve
 * un resumen; si ya había un flush en curso, retorna un resumen vacío sin hacer nada.
 */
export async function flushOutbox(queryClient: QueryClient): Promise<FlushSummary> {
  const summary: FlushSummary = {
    applied: 0,
    conflict: 0,
    rejected: 0,
    blocked: 0,
    attachments: 0,
    stopped: false,
  };
  if (flushing) {
    return summary;
  }
  flushing = true;
  let changed = false;
  // Obras cuyas lecturas (lista de LOTOs + panel) hay que reconciliar al terminar. La cola es
  // UNA FIFO global que sirve varias obras; se invalida por obra para no refrescar de más (#159).
  const changedObras = new Set<number>();
  try {
    // FIFO: procesa la primera pendiente, aplica su veredicto y repite. Al eliminar/actualizar
    // la fila procesada, nextPending() avanza a la siguiente pendiente.
    while (true) {
      const m = await nextPending();
      if (!m || m.seq === undefined) {
        break;
      }

      // Reescritura de referencias a entidades creadas offline: rellena payload[field] con el id
      // de servidor desde el idMap. En FIFO el alta referenciada se procesó ANTES; si aún no
      // resuelve, su alta no produjo id (quedó conflict/rejected) → esta mutación queda huérfana.
      const payload: Record<string, unknown> = { ...m.payload };
      let unresolved = false;
      for (const ref of m.refs) {
        const serverId = await getIdMapping(ref.entity, ref.clientUuid);
        if (serverId === undefined) {
          unresolved = true;
          break;
        }
        payload[ref.field] = serverId;
      }
      if (unresolved) {
        await updateMutation(m.seq, { status: "blocked", errorCode: "DEP_NO_RESUELTA" });
        summary.blocked += 1;
        continue;
      }

      const body: { mutations: PushMutation[] } = {
        mutations: [
          {
            client_mutation_id: m.clientMutationId,
            entity: m.entity,
            op: m.op,
            client_uuid: m.clientUuid,
            base_version: m.baseVersion,
            payload,
          },
        ],
      };

      let result: MutationResult | undefined;
      try {
        // La obra de la mutación gobierna el endpoint: la FIFO global reparte cada mutación al
        // PUSH de SU obra. Un blocked/conflict de una obra no corta el flush de otra: solo un
        // fallo de red (abajo) detiene toda la cola, porque afecta a todas por igual.
        const resp = await api.post<PushResponse>(`/obras/${m.idObra}/sync/mutations`, body);
        result = resp.results[0];
      } catch {
        // Red caída / 401 / infra: cortar el flush y dejar las pendientes intactas para el
        // próximo reconnect. El 401 (NO_AUTENTICADO) lo gestiona el interceptor global, que
        // redirige a login SIN descartar la outbox.
        summary.stopped = true;
        break;
      }
      if (!result) {
        summary.stopped = true; // respuesta malformada: no arriesgar a perder la mutación
        break;
      }

      const attempts = m.attempts + 1;
      if (result.status === "applied") {
        // Registra la traducción identidad local → id de servidor (si el alta creó entidad) para
        // que las mutaciones dependientes reescriban sus referencias, y saca la mutación de la cola.
        if (result.id_mapping && m.clientUuid) {
          await putIdMapping(m.idObra, m.entity, m.clientUuid, result.id_mapping.server_id);
          // La ficha optimista con id temporal (LOTO creado offline) ya no debe consultarse: su id
          // no existe en el servidor. Se retira de la caché de SU obra para que la reconciliación no
          // dispare un 404; la lista refrescada trae el LOTO con su id y número definitivos.
          const tempId = await getTempIdByClientUuid(m.entity, m.clientUuid);
          if (tempId !== undefined) {
            queryClient.removeQueries({ queryKey: [...lotosQueryKey(m.idObra), tempId] });
          }
        }
        await removeMutation(m.seq);
        summary.applied += 1;
      } else if (result.status === "conflict") {
        await updateMutation(m.seq, { status: "conflict", errorCode: result.error_code, attempts });
        summary.conflict += 1;
      } else {
        await updateMutation(m.seq, { status: "rejected", errorCode: result.error_code, attempts });
        // #306 — Un alta RECHAZADA deja la ficha optimista describiendo una intervención que no
        // existe ni existirá: la lista se sana sola con la invalidación de abajo, pero la ficha
        // ABIERTA del id temporal se quedaba en pantalla, y es donde peor sienta. Se retira igual
        // que en `applied` y con la MISMA query key (la caché es por obra). La MUTACIÓN se
        // conserva en `rejected` con su motivo —purgar una LECTURA nunca descarta el trabajo
        // encolado—: es lo que el centro de sincronización lista, y allí acaba el técnico. Quien
        // NAVEGA es la ficha (`features/lotos/`) al observar este estado: `lib/` no importa de
        // `app/` ni navega.
        //
        // Solo el ALTA DE UN LOTO: un candado colocado sin red también tiene identidad local y su
        // propio id temporal (`putLocalRef` con entity "bloqueo"), así que preguntar solo por
        // «¿resuelve algún tempId?» retiraría aquí una query que no es de nadie. Colocar candado y
        // retirar punto conservan exactamente su comportamiento.
        if (m.entity === "loto" && m.op === "create" && m.clientUuid) {
          const tempId = await getTempIdByClientUuid(m.entity, m.clientUuid);
          if (tempId !== undefined) {
            queryClient.removeQueries({ queryKey: [...lotosQueryKey(m.idObra), tempId] });
          }
        }
        summary.rejected += 1;
      }
      changed = true;
      changedObras.add(m.idObra);
    }

    // Adjuntos (#54/#212): sube las fotos capturadas offline al endpoint multipart, una vez conocido
    // el id REAL de su entidad dueña. Se hace TRAS el bucle FIFO para que las altas de este mismo
    // flush ya hayan poblado el idMap. El binario no viaja en el PUSH (el applier solo entiende JSON):
    // se sube aparte a POST /{bloqueos|lotos}/{id}/fotos según `kind`, donde el servidor revalida
    // MIME/tamaño/tope y sigue siendo la única autoridad. Solo con conexión y si el flush no se cortó.
    if (!summary.stopped && getOnline()) {
      const fotos = await listAttachments();
      for (const foto of fotos) {
        if (foto.id === undefined) {
          continue;
        }
        // kind ausente ⇒ "bloqueo" (filas encoladas antes de #212). Gobierna el endpoint y la
        // entidad del idMap con la que se resuelve la identidad local.
        const kind = foto.kind ?? "bloqueo";
        const recurso = kind === "loto" ? "lotos" : "bloqueos";
        // Resuelve la entidad destino: id de servidor directo, o traducido desde la identidad local.
        let destinoId = foto.serverId ?? undefined;
        if (destinoId === undefined && foto.clientUuid) {
          destinoId = await getIdMapping(kind, foto.clientUuid);
        }
        if (destinoId === undefined) {
          // El alta de la entidad aún no se ha aplicado (pendiente o en conflicto): se reintenta en
          // el próximo flush, cuando el idMap resuelva su identidad local.
          continue;
        }

        const form = new FormData();
        form.append("foto", foto.blob);
        // Las fotos de LOTO llevan además el comentario (opcional). La FASE no se envía
        // (#241): la sella el servidor con el estado del LOTO; si un adjunto viejo aún la
        // guarda, se ignora.
        if (kind === "loto" && foto.comentario) {
          form.append("comentario", foto.comentario);
        }
        try {
          await api.postForm<unknown>(`/obras/${foto.idObra}/${recurso}/${destinoId}/fotos`, form);
          await removeAttachment(foto.id);
          summary.attachments += 1;
          changed = true;
          changedObras.add(foto.idObra);
        } catch (err) {
          // Error PERMANENTE de dominio (tope de fotos, MIME/tamaño inválido, entidad inexistente):
          // la foto no prosperará al reintentar → se descarta para no atascar la cola. El candado ya
          // está colocado / el LOTO ya existe; la foto es evidencia, no una invariante de seguridad.
          if (
            err instanceof ApiError &&
            err.status >= 400 &&
            err.status < 500 &&
            err.status !== 401 &&
            err.status !== 429
          ) {
            await removeAttachment(foto.id);
            changed = true;
            continue;
          }
          // Red caída / 401 (sesión caducada) / 5xx / 429: cortar y conservar la foto para el próximo
          // reconnect. El interceptor global gestiona el 401 (redirige a login SIN descartar la cola).
          summary.stopped = true;
          break;
        }
      }
    }
  } finally {
    flushing = false;
  }

  // Reconciliación: si algo cambió en el servidor y seguimos online (el flush no se cortó por
  // red), refresca las lecturas afectadas SOLO de las obras tocadas. Invalidar lotosQueryKey(obra)
  // cubre por prefijo la lista y toda ficha [...lotos(obra), id]; el panel deriva de los LOTOs y
  // sus candados. Cada obra se reconcilia por separado (la cola global sirve varias a la vez).
  if (changed && !summary.stopped) {
    for (const idObra of changedObras) {
      await queryClient.invalidateQueries({ queryKey: lotosQueryKey(idObra) });
      await queryClient.invalidateQueries({ queryKey: dashboardQueryKey(idObra) });
    }
  }
  return summary;
}
